import { useEffect, useState } from 'react';
import Marquee from 'react-fast-marquee';
import { Reveal, ParallaxMouse } from '@/components/Reveal';
import {
  getPublic,
  readPublicCache,
  toAssetUrl,
  writePublicCache,
} from '@/data/runtimeApi';

const getInitialPayload = () => {
  const cachedPayload = readPublicCache('clients');
  return cachedPayload && Array.isArray(cachedPayload.clients)
    ? cachedPayload
    : { clients: [], speed: 40 };
};

export const ClientsCarousel = () => {
  const [initialPayload] = useState(getInitialPayload);
  const [clients, setClients] = useState(initialPayload.clients);
  const [speed, setSpeed] = useState(initialPayload.speed || 40);

  useEffect(() => {
    let alive = true;

    getPublic('/clients')
      .then(({ data }) => {
        if (!alive) return;
        if (!Array.isArray(data?.clients)) {
          throw new Error('La API pública de clientes devolvió un formato inválido.');
        }

        const nextClients = data.clients.map((client) => ({
          ...client,
          logo: toAssetUrl(client.logo),
        }));
        const nextSpeed =
          typeof data.speed === 'number' && data.speed > 0 ? data.speed : 40;

        setClients(nextClients);
        setSpeed(nextSpeed);
        writePublicCache('clients', {
          clients: nextClients,
          speed: nextSpeed,
        });
      })
      .catch((error) => {
        console.error('[public clients] No se pudieron cargar los clientes administrados.', error);
        // Keep the last successful DB snapshot. Never resurrect bundled logos.
      });

    return () => {
      alive = false;
    };
  }, []);

  return (
    <section
      className="relative text-[#58677A] bg-no-repeat bg-bottom bg-[length:100%_auto]"
      data-testid="home-clients-section"
      style={{
        backgroundColor: '#FFFFFF',
        backgroundImage: "url('/assets/backgrounds/paper-bg-3.jpg')",
      }}
    >
      {clients.length > 0 && (
        <div className="pt-6 sm:pt-8 lg:pt-10 pb-10 lg:pb-14">
          <Reveal>
            <div data-testid="clients-marquee">
              <Marquee gradient={false} speed={speed} pauseOnHover>
                {clients.map((client) => {
                  const widthScale =
                    (typeof client.width_pct === 'number' ? client.width_pct : 100) /
                    100;

                  return (
                    <div
                      key={client.id || client.name}
                      className="mx-6 sm:mx-10 lg:mx-14 flex items-center justify-center h-16 sm:h-20"
                      title={client.name}
                      aria-label={client.name}
                    >
                      <div
                        className="max-h-[38px] sm:max-h-[51px] h-[38px] sm:h-[51px] w-[calc(120px*var(--wpct))] sm:w-[calc(160px*var(--wpct))] opacity-90 hover:opacity-100 transition-opacity duration-300"
                        style={{
                          '--wpct': widthScale,
                          backgroundColor: '#3A4657',
                          WebkitMaskImage: `url(${client.logo})`,
                          maskImage: `url(${client.logo})`,
                          WebkitMaskRepeat: 'no-repeat',
                          maskRepeat: 'no-repeat',
                          WebkitMaskPosition: 'center',
                          maskPosition: 'center',
                          WebkitMaskSize: 'contain',
                          maskSize: 'contain',
                        }}
                        data-testid={`client-logo-${client.name}`}
                      />
                    </div>
                  );
                })}
              </Marquee>
            </div>
          </Reveal>
        </div>
      )}

      <div className="container-link pb-[16px] lg:pb-[26px]">
        <Reveal>
          <ParallaxMouse strength={28} className="flex justify-center">
            <div className="mb-[40px] sm:mb-[80px]">
              <img
                src="/assets/backgrounds/seccion-aportamos.png"
                alt="Aportamos una mirada externa a tu marca"
                className="block h-auto select-none pointer-events-none"
                style={{ width: '84%', maxWidth: '1253px', margin: '0 auto' }}
                data-testid="home-mirada-externa-graphic"
                draggable={false}
              />
            </div>
          </ParallaxMouse>
        </Reveal>
      </div>
    </section>
  );
};
